import { Modal } from "react-bootstrap";
import { modal } from "@/utils/constant";
import { useEffect, useState } from "react";
import { connection, CONNECTION_THRESHOLDS } from "@/utils/constant";
import { BsFillCheckCircleFill, BsXCircle } from "react-icons/bs";
import {
  measureUploadSpeed,
  measurePingAndPacketLoss,
  checkDatabaseStatus,
} from "../../utils/custom";

function ConnectionModel({
  hide,
  show,
  internetSpeed,
  setSpeed,
  speed,
  status,
  setStatus,
  socket,
}) {
  const [btnText, setBtnText] = useState("Start the Test");

  useEffect(() => {
    if (show) {
      const handleClick = async () => {
        setSpeed([]);
        setStatus("");
        setBtnText("Test in Progress");
        let IsmeasureDownloadSpeed = 0;
        const testFileUrl = `${
          process.env.REACT_APP_SUPABASE_URL
        }/storage/v1/object/public/vfner-admin//neture.jpg?a=${Math.random()}`;
        const startDownload = new Date().getTime();
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        fetch(testFileUrl, { signal: controller.signal })
          .then((response) => response.blob())
          .then(async (data) => {
            const endDownload = new Date().getTime();
            const durationInSeconds = (endDownload - startDownload) / 1000;
            const fileSizeInBits = data.size * 18;
            IsmeasureDownloadSpeed =
              fileSizeInBits / (1024 * 1024) / durationInSeconds;
            const IsmeasureUploadSpeed = await measureUploadSpeed();
            const IsmeasurePingAndPacketLoss = await measurePingAndPacketLoss();
            let responseServerConnection = false;
            if (socket && socket.connected) {
              socket.emit("check-supabase-connection", {}, (response) => {
                if (response && response.success) {
                  responseServerConnection = "Good";
                } else {
                  responseServerConnection = "Bad";
                }
              });
            } else {
              responseServerConnection = "Bad";
            }
            const responseDatabaseConnection = await checkDatabaseStatus(
              socket
            );
            setStatus(`Database connection: ${responseDatabaseConnection}`);
            setSpeed([
              IsmeasureDownloadSpeed.toFixed(2),
              IsmeasureUploadSpeed,
              IsmeasurePingAndPacketLoss.ping,
              IsmeasurePingAndPacketLoss.packetLoss,
              responseServerConnection,
            ]);
            setBtnText("Test Again");
            clearTimeout(timeoutId);
          })
          .catch((error) => {
            clearTimeout(timeoutId);
            setStatus(`Database connection: Bad`);
            setSpeed([0, 0, 300, 0, "Bad"]);
          });
      };
      if (internetSpeed) {
        handleClick();
      } else {
        setSpeed([0, 0, 300, 0, "Bad"]);
        setStatus(`Database connection: Bad`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <>
      <Modal show={show} onHide={hide} size="xl" centered>
        <Modal.Header className="bg-secondary" closeButton>
          <Modal.Title>{modal?.testYourConnection}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-10 px-lg-14">
          <div className="mb-8 vstack gap-8">
            <p className="text-md">
              {
                "Use this tool to check your network readiness and ensure a seamless vFNER experience."
              }
            </p>
            <div>
              <h5 className="text-lg font-bolder">{"Connection Speed"}</h5>
              <p className="text-sm mt-2 mb-3">
                {btnText === "Start the Test" && speed?.length === 0
                  ? connection?.readyToTest
                  : btnText === "Test in Progress" && speed?.length === 0
                  ? connection?.connectionSpeedText
                  : `Download Speed: ${speed[0]} Mbps | Upload Speed: ${speed[1]} Mbps`}
              </p>
              {speed[0] >= CONNECTION_THRESHOLDS.DOWNLOAD_SPEED_GREEN ? (
                <span className="pe-2 lh-none text-success font-bolder text-sm hstack">
                  <BsFillCheckCircleFill
                    className="text-success me-2"
                    size={18}
                  />
                  {connection?.connectionSpeedSuccessText}
                </span>
              ) : speed[0] >= CONNECTION_THRESHOLDS.DOWNLOAD_SPEED_RED && speed[0] < CONNECTION_THRESHOLDS.DOWNLOAD_SPEED_GREEN ? (
                <span className="pe-2 lh-none text-yellow-orange font-bolder text-sm hstack">
                  <BsFillCheckCircleFill
                    className="text-yellow-orange me-2"
                    size={18}
                  />
                  {connection?.connectionSpeedWarningText}
                </span>
              ) : speed[0] < CONNECTION_THRESHOLDS.DOWNLOAD_SPEED_RED ? (
                <span className="pe-2 lh-none text-danger font-bolder text-sm hstack">
                  <BsXCircle className="text-danger me-2" size={18} />
                  {connection?.connectionSpeedErrorText}
                </span>
              ) : (
                ""
              )}
            </div>

            <div>
              <h5 className="text-lg font-bolder">
                {"Network Responsiveness"}
              </h5>
              <p className="text-sm mt-2 mb-3">
                {btnText === "Start the Test" && speed.length === 0
                  ? connection?.readyToTest
                  : btnText === "Test in Progress" && speed.length === 0
                  ? connection?.networkResponsivenessText
                  : `Ping: ${speed[2]} ms | Packet Loss: ${speed[3]}%`}
              </p>
              {speed[2] < CONNECTION_THRESHOLDS.PING_WARNING ? (
                <span className="pe-2 lh-none text-success font-bolder text-sm hstack">
                  <BsFillCheckCircleFill
                    className="text-success me-2"
                    size={18}
                  />
                  {connection?.networkResponsivenessSuccessText}
                </span>
              ) : speed[2] >= CONNECTION_THRESHOLDS.PING_WARNING && speed[2] < CONNECTION_THRESHOLDS.PING_ERROR ? (
                <span className="pe-2 lh-none text-yellow-orange font-bolder text-sm hstack">
                  <BsFillCheckCircleFill
                    className="text-yellow-orange me-2"
                    size={18}
                  />
                  {connection?.networkResponsivenessWarningText}
                </span>
              ) : speed[2] >= CONNECTION_THRESHOLDS.PING_ERROR ? (
                <span className="pe-2 lh-none text-danger font-bolder text-sm hstack">
                  <BsXCircle className="text-danger me-2" size={18} />
                  {connection?.networkResponsivenessErrorText}
                </span>
              ) : (
                ""
              )}
            </div>

            <div>
              <h5 className="text-lg font-bolder">{"Server Connection"}</h5>
              <p className="text-sm mt-2 mb-3">
                {btnText === "Start the Test" && (speed[4] === "" || !speed[4])
                  ? connection?.readyToTest
                  : btnText === "Test in Progress" &&
                    (speed[4] === "" || !speed[4])
                  ? connection?.serverConnectionText
                  : `Server Connection: ${speed[4]}`}
              </p>
              {speed[4] === "Good" ? (
                <span className="pe-2 lh-none text-success font-bolder text-sm hstack">
                  <BsFillCheckCircleFill
                    className="text-success me-2"
                    size={18}
                  />
                  {connection?.serverConnectionSuccessText}
                </span>
              ) : speed[4] === "Average" ? (
                <span className="pe-2 lh-none text-yellow-orange font-bolder text-sm hstack">
                  <BsFillCheckCircleFill
                    className="text-yellow-orange me-2"
                    size={18}
                  />
                  {connection?.serverConnectionWarningText}
                </span>
              ) : speed[4] === "Bad" ? (
                <span className="pe-2 lh-none text-danger font-bolder text-sm hstack">
                  <BsXCircle className="text-danger me-2" size={18} />
                  {connection?.serverConnectionErrorText}
                </span>
              ) : (
                ""
              )}
            </div>

            <div>
              <h5 className="text-lg font-bolder">{"Database connection"}</h5>
              <p className="text-sm mt-2 mb-3">
                {btnText === "Start the Test" && status === ""
                  ? connection?.readyToTest
                  : btnText === "Test in Progress" && status === ""
                  ? connection?.databaseConnectionText
                  : status}
              </p>
              {status === "Database connection: Good" ? (
                <span className="pe-2 lh-none text-success font-bolder text-sm hstack">
                  <BsFillCheckCircleFill
                    className="text-success me-2"
                    size={18}
                  />
                  {connection?.databaseConnectionSuccessText}
                </span>
              ) : status === "Database connection: Average" ? (
                <span className="pe-2 lh-none text-yellow-orange font-bolder text-sm hstack">
                  <BsFillCheckCircleFill
                    className="text-yellow-orange me-2"
                    size={18}
                  />
                  {connection?.databaseConnectionWarningText}
                </span>
              ) : status === "Database connection: Bad" ? (
                <span className="pe-2 lh-none text-danger font-bolder text-sm hstack">
                  <BsXCircle className="text-danger me-2" size={18} />
                  {connection?.databaseConnectionErrorText}
                </span>
              ) : (
                ""
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
export default ConnectionModel;
